import { useEffect, useState, useMemo } from "react";
import { Database, Edit, Trash2, Plus, ArrowLeft, Search } from "lucide-react";
import { getAllDBConfigs, deleteDBConfig,getDBConfigById } from "../../../apiWithDBConfig";
import { toast } from "react-toastify";
import DBWorkspace from "./DBWorkspace";
import DBConfigFormModal from "./DBConfigFormModal";
import { useIngestionJobs } from "../ingestion/IngestionJobsContext";
import { IngestionQueueStrip } from "../ingestion/IngestionFeedback";

export default function DatabaseManagementPage() {
  const [configs, setConfigs] = useState([]);
  const [activeConfigId, setActiveConfigId] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [loadingEditConfig, setLoadingEditConfig] = useState(false);
  const [search, setSearch] = useState("");
  const { registerJob } = useIngestionJobs({ resourceTypes: ["source_cleanup"] });

  /* ================= LOAD DATABASES ================= */

  const loadConfigs = async () => {
    setLoadingConfigs(true);
    try {
      const { data } = await getAllDBConfigs();
      setConfigs(data || []);
    } finally {
      setLoadingConfigs(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  /* ================= FILTER ================= */

  const filteredConfigs = useMemo(() => {
    return configs.filter((c) =>
      c.database_name.toLowerCase().includes(search.toLowerCase())
    );
  }, [configs, search]);

  const getHostDisplay = (cfg) => {
    if (cfg.db_type === "bigquery" || cfg.db_type === "snowflake" || cfg.db_type === "databricks" || cfg.db_type === "tableau" || !cfg.port) {
      return cfg.host;
    }
    return `${cfg.host}:${cfg.port}`;
  };

  const handleEditConfig = async (cfg) => {
    setLoadingEditConfig(true);
    try {
      const { data } = await getDBConfigById(cfg.id);
      setEditConfig(data || cfg);
      setOpenForm(true);
    } catch {
      toast.error("Failed to load database details");
    } finally {
      setLoadingEditConfig(false);
    }
  };

  /* ================= VIEW SWITCH ================= */

  if (activeConfigId) {
    return (
      <div className="h-full flex flex-col">

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setActiveConfigId(null)}
            className="p-2 rounded-md hover:bg-muted transition"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <h2 className="text-lg font-semibold">Database Workspace</h2>
        </div>

        {/* Workspace */}
        <div className="flex-1 overflow-hidden">
          <DBWorkspace configId={activeConfigId} />
        </div>

    
      </div>
    );
}

  /* ================= DATABASE GRID VIEW ================= */

  return (
    <div className="h-full flex flex-col p-6 bg-[var(--background)] rounded-2xl" style={{ backgroundColor: 'var(--background)' }}>

      <div className="flex items-start justify-between mb-6">

        <div className="flex gap-3">
          <div className="p-4 rounded-xl bg-[var(--primary)]/10" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)' }}>
            <Database className="w-4 h-4 text-[var(--primary)]" style={{ color: 'var(--primary)' }} />
          </div>

          <div>
            <h4 className="text-xl font-semibold" style={{ color: 'var(--foreground)' }}>Databases</h4>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              {configs.length} connections configured
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            setEditConfig(null);
            setOpenForm(true);
          }}
          disabled={loadingEditConfig}
          className="flex items-center gap-2 px-5 py-2 rounded-xl bg-[var(--primary)] text-[var(--primary-foreground)] hover:opacity-90"
          style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
        >
          <Plus className="w-4 h-4" />
          Add Database
        </button>

      </div>


      {/* Search */}
      <IngestionQueueStrip className="mb-4" resourceTypes={["source_cleanup"]} />
      <div className="relative mb-6 w-full max-w-[520px]">
        <Search className="absolute left-3 top-2.5 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
        <input
          type="text"
          placeholder="Search databases..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border bg-[var(--background)]"
          style={{ 
            backgroundColor: 'var(--background)', 
            borderColor: 'var(--border)', 
            color: 'var(--foreground)'
          }}
        />
      </div>

      {/* Grid */}
      <div className="no-scrollbar flex-1 overflow-y-auto">
        {loadingConfigs ? (
          <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading databases...</div>
        ) : filteredConfigs.length === 0 ? (
          <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            No databases found.
          </div>
        ) : (
<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">            {filteredConfigs.map((cfg) => (

              <div
  key={cfg.id}
  onClick={() => setActiveConfigId(cfg.id)}
  className="group cursor-pointer flex flex-col mt-2"
  style={{
    backgroundColor: 'var(--card)',
    borderColor: 'var(--border)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '16px',
    padding: '16px',
    minHeight: '150px',
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'hidden',
  }}
  onMouseEnter={e => {
    e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)';
    e.currentTarget.style.boxShadow = '0 8px 24px rgba(99,102,241,0.15)';
    e.currentTarget.style.transform = 'translateY(-2px)';
  }}
  onMouseLeave={e => {
    e.currentTarget.style.borderColor = 'var(--border)';
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.transform = 'translateY(0)';
  }}
>
  {/* Top gradient accent bar */}
  <div style={{
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #6366f1, #a876df)',
    borderRadius: '16px 16px 0 0',
    opacity: 0.7,
  }} />

  {/* HEADER */}
  <div className="flex items-start justify-between mb-3">
    <div
      style={{
        padding: '8px',
        borderRadius: '10px',
        backgroundColor: 'rgba(99,102,241,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Database className="w-5 h-5" style={{ color: 'var(--primary)' }} />
    </div>

    {/* Arrow */}
    <span style={{
      color: 'var(--muted-foreground)',
      fontSize: '18px',
      transition: 'transform 0.2s ease, color 0.2s ease',
    }}
      className="group-hover:text-primary"
    >→</span>
  </div>

  {/* DB Name */}
  <h3
    className="text-sm font-bold truncate mb-1"
    style={{ color: 'var(--foreground)', letterSpacing: '-0.01em' }}
  >
    {cfg.database_name}
  </h3>

  {/* HOST */}
  <p className="text-xs truncate mb-3" style={{ color: 'var(--muted-foreground)' }}>
    {getHostDisplay(cfg)}
  </p>

  {/* Footer row */}
  {/* Footer row */}
<div className="flex items-center justify-between mt-auto">
  <span
    className="text-xs font-medium px-2.5 py-1 rounded-full"
    style={{
      backgroundColor: 'rgba(99,102,241,0.12)',
      color: 'var(--primary)',
      letterSpacing: '0.02em',
    }}
  >
    {cfg.db_type}
  </span>

  {/* Edit + Delete buttons — visible on hover */}
  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
    <button
      onClick={(e) => {
        e.stopPropagation(); // prevent card click
        handleEditConfig(cfg);
      }}
      disabled={loadingEditConfig}
      className="p-1.5 rounded-lg hover:bg-[var(--muted)] transition"
      title="Edit"
    >
      <Edit className="w-3.5 h-3.5" style={{ color: 'var(--muted-foreground)' }} />
    </button>
    <button
      onClick={async (e) => {
        e.stopPropagation(); // prevent card click
        if (!window.confirm(`Delete "${cfg.database_name}"?`)) return;
        try {
          const { data } = await deleteDBConfig(cfg.id);
          if (data?.ingestion) {
            registerJob({
              summary: data.ingestion,
              sourceDbId: cfg.id,
              resourceId: cfg.id,
              label: cfg.database_name,
            });
          }
          setConfigs((current) => current.filter((config) => config.id !== cfg.id));
          toast.success("Database deleted");
        } catch (err) {
  const status = err.response?.status;
  const msg = err.response?.data?.message || "";

  if (status === 409) {
    // Extract numbers from "assigned to 1 user(s) and 0 group(s)"
    const users = msg.match(/(\d+) user/)?.[1];
    const groups = msg.match(/(\d+) group/)?.[1];

    if (users > 0 && groups > 0) {
      toast.error(`"${cfg.database_name}" is assigned to ${users} user(s) and ${groups} group(s). Unassign them first before deleting.`);
    } else if (users > 0) {
      toast.error(`"${cfg.database_name}" is assigned to ${users} user(s). Unassign them first before deleting.`);
    } else if (groups > 0) {
      toast.error(`"${cfg.database_name}" is assigned to ${groups} group(s). Unassign them first before deleting.`);
    } else {
      toast.error(`"${cfg.database_name}" is currently in use and cannot be deleted.`);
    }
  } else if (status === 404) {
    toast.error("This database no longer exists. Please refresh the page.");
  } else {
    toast.error("Something went wrong while deleting. Please try again.");
  }
}
      }}
      className="p-1.5 rounded-lg hover:bg-red-500/10 transition"
      title="Delete"
    >
      <Trash2 className="w-3.5 h-3.5 text-red-500" />
    </button>
  </div>
</div>

</div>

 ))}
      </div>
        )}
      </div>

      {/* Modal */}
      {openForm && (
        <DBConfigFormModal
          config={editConfig}
          onClose={() => setOpenForm(false)}
          onSaved={async (savedId) => {
            await loadConfigs();
            setOpenForm(false);
            if (savedId) setActiveConfigId(savedId); // ← auto-navigate to workspace

          }}
        />
      )}

    </div>
  );
}
